import {
  CHAT_UPLOAD_MAX_IMAGE_FILE_BYTES,
  classifyChatUploadFile,
} from './chatUploadLimits';

export const CHAT_UPLOAD_MAX_IMAGE_DIMENSION = 1920;
export const CHAT_UPLOAD_JPEG_QUALITY = 0.85;

export function loadImageElement(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('decode_failed'));
    };
    img.src = url;
  });
}

function buildResizedJpegFile(blob, sourceFile) {
  const baseName = String(sourceFile.name || 'image')
    .replace(/\.[^.]+$/, '') || 'image';
  return new File([blob], `${baseName}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
}

/**
 * Thu nhỏ ảnh chat (max cạnh, JPEG) trước khi upload.
 * GIF giữ nguyên — tránh mất animation.
 */
export async function prepareChatImageFileForUpload(
  file,
  {
    maxDimension = CHAT_UPLOAD_MAX_IMAGE_DIMENSION,
    quality = CHAT_UPLOAD_JPEG_QUALITY,
    maxBytes = CHAT_UPLOAD_MAX_IMAGE_FILE_BYTES,
  } = {},
) {
  if (!file || classifyChatUploadFile(file) !== 'image') return file;

  const ext = String(file.name || '').split('.').pop().toLowerCase();
  const mime = String(file.type || '').toLowerCase();
  if (mime === 'image/gif' || ext === 'gif') return file;

  try {
    const img = await loadImageElement(file);
    const maxSide = Math.max(img.width, img.height);
    let scale = Math.min(1, maxDimension / maxSide);
    let jpegQuality = quality;

    for (let attempt = 0; attempt < 10; attempt += 1) {
      const width = Math.max(1, Math.floor(img.width * scale));
      const height = Math.max(1, Math.floor(img.height * scale));
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) break;
      ctx.drawImage(img, 0, 0, width, height);

      // eslint-disable-next-line no-await-in-loop
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', jpegQuality);
      });

      if (!blob || blob.size <= 0) break;

      const withinDimension = maxSide <= maxDimension || scale < 1;
      const withinBytes = blob.size <= maxBytes;
      const alreadyJpeg = mime === 'image/jpeg' || ext === 'jpg' || ext === 'jpeg';
      if (
        withinBytes
        && withinDimension
        && alreadyJpeg
        && Math.abs(scale - 1) < 0.001
        && blob.size >= file.size * 0.95
      ) {
        return file;
      }

      if (withinBytes) {
        return buildResizedJpegFile(blob, file);
      }

      scale *= 0.85;
      jpegQuality = Math.max(0.55, jpegQuality - 0.05);
    }
  } catch (e) {
    void e;
  }

  return file;
}

export async function prepareChatUploadFiles(files) {
  if (!files || !files.length) return [];
  const prepared = [];
  for (let i = 0; i < files.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    const next = await prepareChatImageFileForUpload(files[i]);
    prepared.push(next);
  }
  return prepared;
}
